/**
 * P0-13 · StoreImage — the one way product/media imagery renders.
 *
 * Wraps `next/image` with the custom media-origin loader, named `sizes`
 * presets (so `srcset` selection actually matches layout), and blur
 * placeholder wiring from `Media.blurDataUrl`. `alt` is required by type —
 * pass `""` explicitly for purely decorative images. Consumed by the media
 * library (P1-11), catalogue cards (P2), and the PDP gallery (P3).
 *
 * P18-25 · A preset may also declare its own `srcset` width set
 * (`STORE_IMAGE_WIDTHS`). `next/image` builds a candidate list from the global
 * `images.imageSizes`/`deviceSizes` with a floor and no ceiling, so a product
 * card was advertising nine widths up to 2048 px for a box that is never wider
 * than 268 px. A preset with a width set renders the `<img>` itself — same
 * loader, same attributes, our candidate list.
 */
import Image, { type ImageProps } from "next/image";

import imageLoader from "~/lib/image-loader";
import { cn } from "~/lib/utils";

/**
 * How wide the image renders per breakpoint — feeds `srcset` selection.
 * Presets over free-form strings so usage stays consistent across surfaces.
 */
export const STORE_IMAGE_SIZES = {
  /** Fixed small thumbnail (admin lists, picker tiles). */
  thumb: "64px",
  /** Product editor sidebar preview, stacked at smaller viewports. */
  adminPreview: "(min-width: 1280px) 268px, (min-width: 768px) calc(100vw - 320px), calc(100vw - 64px)",
  /** Product-card media in the PLP list view (`w-20` row thumbnail, P2-15). */
  listThumb: "80px",
  /** Brand/manufacturer logo on a trust strip (~160px intrinsic, 40px tall). */
  logo: "160px",
  /** Catalogue/product card in a 2-up → 4-up responsive grid. */
  card: "(min-width: 1280px) 25vw, (min-width: 640px) 33vw, 50vw",
  /** Blog/journal cover card — 1-up mobile, 2-up tablet, 3-up desktop (P2-15). */
  blogCard: "(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw",
  /** PDP gallery pane — half the viewport on desktop, full on mobile. */
  gallery: "(min-width: 1024px) 50vw, 100vw",
  /** PDP: max-w-6xl (1152px), 32px page padding, 58% desktop column. */
  pdpGallery: "(min-width: 1152px) 649.6px, (min-width: 1024px) calc(58vw - 18.56px), calc(100vw - 32px)",
  /** Side thumbnails remove a 64px rail + 12px gap from md upward. */
  pdpGallerySide: "(min-width: 1152px) 573.6px, (min-width: 1024px) calc(58vw - 94.56px), (min-width: 768px) calc(100vw - 108px), calc(100vw - 32px)",
  /** Full-bleed hero/banner. */
  hero: "100vw",
  /**
   * P18-25 · Product-card media in a product grid — the `card` preset with a
   * ceiling. A card lives inside `max-w-6xl` (`home-section`, the PLP frame),
   * so above `lg` it is at most (1152 − 32 − 3×16) / 4 = **268 px**, not the
   * 25vw the old string declared: on a 2560 px display 25vw is 640, and the
   * browser was picking a 640-wide rendition for a 268 px box. 272px caps it;
   * the two smaller breakpoints are unchanged from `card`.
   */
  productCard: "(min-width: 1024px) 272px, (min-width: 640px) 33vw, 50vw",
  /** P18-25 · Product-card media in the PLP list row (`w-20` = 80 px). */
  productCardThumb: "80px",
} as const;
export type StoreImageSizePreset = keyof typeof STORE_IMAGE_SIZES;

/**
 * P18-25 · Card-scoped `srcset` width sets — where a card's candidates stop.
 *
 * A preset listed here renders a plain `<img>` whose `srcset` is built from
 * exactly these widths through the same media loader `next/image` uses, instead
 * of letting `next/image` generate the candidate list.
 *
 * **Why not `next.config`'s `images.imageSizes` / `deviceSizes`, as P18-25
 * assumed.** Those two arrays are global and `next/image` derives a candidate
 * list from them with a *floor* and no ceiling: `allSizes.filter(s => s >=
 * deviceSizes[0] * smallestVwInSizes)` (`next/dist/shared/lib/get-img-props.js`
 * `getWidths`). For the card's `50vw` floor that is every bucket from 256 to
 * 2048 — **nine candidates, 1,059 B of `srcset` per image** — and there is no
 * per-image override: `getImgProps` deletes any `srcSet` prop and reads the
 * config off `process.env.__NEXT_IMAGE_OPTS`. Trimming the arrays to stop at
 * 512 would trim the hero and the PDP gallery with it; *adding* a card bucket
 * widens every other preset's list. The ceiling has to be per-variant, so it
 * lives here, next to the `sizes` string it belongs to.
 *
 * `hover` is the single width the hover-swap layer is served at (see
 * `cardHoverImageUrl`) — one URL, no candidate list, because that layer is a
 * transient affordance and is only ever fetched on a fine pointer.
 */
export const STORE_IMAGE_WIDTHS = {
  // 268 px box, so 512 is 1.9× the largest layout width; 256 covers a 2-up
  // phone card at 1×–1.6× and 384 at 2×.
  productCard: { srcSet: [256, 384, 512], hover: 384 },
  // 80 px box: 96 for 1×, 160 for 2×.
  productCardThumb: { srcSet: [96, 160], hover: 160 },
} as const satisfies Partial<
  Record<
    StoreImageSizePreset,
    { srcSet: readonly number[]; hover: number }
  >
>;
type FixedWidthPreset = keyof typeof STORE_IMAGE_WIDTHS;

function fixedWidths(preset: StoreImageSizePreset) {
  return (
    STORE_IMAGE_WIDTHS as Partial<
      Record<StoreImageSizePreset, (typeof STORE_IMAGE_WIDTHS)[FixedWidthPreset]>
    >
  )[preset];
}

/**
 * P18-25 · The one URL a card's hover-swap layer is painted from.
 *
 * The layer is a CSS background rather than a second `<img>` (see
 * `card-preview.tsx`), so it carries no candidate list — one width, chosen from
 * the variant's own set.
 */
export function cardHoverImageUrl(
  src: string,
  preset: StoreImageSizePreset,
): string {
  return imageLoader({ src, width: fixedWidths(preset)?.hover ?? 384 });
}

export interface StoreImageProps {
  /** Absolute media URL (from `getPublicUrl`) or app-local static path. */
  src: string;
  /** Required for a11y; use "" only for purely decorative images. */
  alt: string;
  /** Layout preset driving the responsive `sizes` attribute. */
  preset?: StoreImageSizePreset;
  /** Tiny base64 placeholder from `Media.blurDataUrl`; empty renders plain. */
  blurDataUrl?: string | null;
  /** Fill the positioned parent instead of fixed dimensions. */
  fill?: boolean;
  width?: number;
  height?: number;
  priority?: boolean;
  loading?: ImageProps["loading"];
  onError?: ImageProps["onError"];
  draggable?: boolean;
  style?: ImageProps["style"];
  className?: string;
}

export function StoreImage({
  src,
  alt,
  preset = "card",
  blurDataUrl,
  fill,
  width,
  height,
  priority,
  loading,
  onError,
  draggable,
  style,
  className,
}: StoreImageProps) {
  const widths = fixedWidths(preset);
  if (widths) {
    // P18-25 · A preset with its own width set renders the `<img>` directly.
    // Same loader, same attributes `next/image` would emit — only the candidate
    // list is ours (see STORE_IMAGE_WIDTHS for why it cannot be config).
    // Hook-free and prop-only, so it still renders on either side of an RSC
    // boundary. `fill` positioning moves from next/image's ~95 B inline style
    // to four utilities, which on 24 cards is another ~1.7 KB of document.
    const blur = blurDataUrl
      ? {
          backgroundImage: `url("${blurDataUrl}")`,
          backgroundSize: "cover",
          backgroundPosition: "50% 50%",
          backgroundRepeat: "no-repeat",
        }
      : null;
    return (
      // `no-img-element` is exactly the rule this branch exists to override:
      // owning the candidate list is the optimisation, not a missed one.
      // eslint-disable-next-line @next/next/no-img-element
      <img
        alt={alt}
        loading={priority ? "eager" : (loading ?? "lazy")}
        decoding="async"
        fetchPriority={priority ? "high" : undefined}
        sizes={STORE_IMAGE_SIZES[preset]}
        srcSet={widths.srcSet
          .map((w) => `${imageLoader({ src, width: w })} ${w}w`)
          .join(", ")}
        src={imageLoader({
          src,
          width: widths.srcSet[widths.srcSet.length - 1]!,
        })}
        {...(fill ? {} : { width, height })}
        onError={onError}
        draggable={draggable}
        className={cn(fill && "absolute inset-0 h-full w-full", className)}
        style={blur || style ? { ...blur, ...style } : undefined}
      />
    );
  }
  return (
    // No `loader` prop: it's registered globally via next.config's
    // `images.loaderFile`, and passing the function here from a Server Component
    // trips React's "functions can't cross to a Client Component" boundary
    // (crashes any RSC block that renders a real image).
    <Image
      src={src}
      alt={alt}
      sizes={STORE_IMAGE_SIZES[preset]}
      {...(fill ? { fill: true } : { width, height })}
      {...(blurDataUrl
        ? { placeholder: "blur" as const, blurDataURL: blurDataUrl }
        : {})}
      priority={priority}
      loading={loading}
      onError={onError}
      draggable={draggable}
      style={style}
      className={className}
    />
  );
}
